import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { buildApiUrl, apiConfig } from '../config/api';
import ServiceHistory from '../components/ServiceHistory';

// Member detail interfaces
interface MemberDetail {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
  membership: string;
  createdAt: string;
  groups: GroupParticipation[];
  serving: ServingHistory[];
  giving: GivingHistory[];
  attendance: AttendanceHistory[];
  discoverGrace: DiscoverGraceAttendance[];
  journeyStage: string;
  lastActivity: string;
}

interface GroupParticipation {
  groupId: string;
  groupName: string;
  role: string;
  joinedDate: string;
  meetingsAttended: number;
  totalMeetings: number;
  attendanceRate: number;
  isActive: boolean;
}

interface ServingHistory {
  teamId: string;
  teamName: string;
  description: string;
  joinedAt: string;
  position: string;
  totalServices: number;
  lastService: string | null;
  firstService: string | null;
  monthsActive: number;
  frequencyLevel: 'No Services' | 'Occasional' | 'Moderate' | 'Active' | 'Very Active' | 'No Data';
  status: 'Current' | 'Recent' | 'Inactive' | 'Historical' | 'Unknown';
}

interface GivingHistory {
  year: number;
  totalAmount: number;
  frequency: number;
  lastGift: string;
  averageGift: number;
}

interface AttendanceHistory {
  eventName: string;
  eventDate: string;
  eventType: string;
  attended: boolean;
}

interface DiscoverGraceAttendance {
  sessionNumber: number;
  sessionDate: string;
  attended: boolean;
  registered: boolean;
}

const MemberDetail: React.FC = () => {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const [member, setMember] = useState<MemberDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (memberId) {
      fetchMemberDetails(memberId);
    }
  }, [memberId]);

  const fetchMemberDetails = async (id: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(buildApiUrl(apiConfig.endpoints.memberDetails.get(id)));
      const data = await response.json();

      if (data.success) {
        setMember(data.data);
      } else {
        setError(data.error?.message || 'Failed to fetch member details');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getJourneyStageColor = (stage: string) => {
    switch (stage) {
      case 'Leader': return 'bg-purple-100 text-purple-800';
      case 'Partner': return 'bg-blue-100 text-blue-800';
      case 'Engaged Member': return 'bg-green-100 text-green-800';
      case 'Member': return 'bg-yellow-100 text-yellow-800';
      case 'Visitor': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading member details...</p>
        </div>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Member</h2>
          <p className="text-gray-600 mb-4">{error || 'Member not found'}</p>
          <button
            onClick={() => navigate('/journey-analytics')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Journey Analytics
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '📊' },
    { id: 'groups', name: 'Groups', icon: '👥' },
    { id: 'serving', name: 'Serving', icon: '🤝' },
    { id: 'giving', name: 'Giving', icon: '💰' },
    { id: 'attendance', name: 'Attendance', icon: '📅' },
    { id: 'discoverGrace', name: 'Discover Grace', icon: '🎓' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <button
                  onClick={() => navigate('/journey-analytics')}
                  className="text-blue-600 hover:text-blue-700 mb-2 flex items-center"
                >
                  ← Back to Journey Analytics
                </button>
                <h1 className="text-3xl font-bold text-gray-900">
                  {member.firstName} {member.lastName}
                </h1>
                <p className="text-gray-600 mt-1">{member.email}</p>
              </div>
              <div className="text-right">
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getJourneyStageColor(member.journeyStage)}`}>
                  {member.journeyStage}
                </span>
                <p className="text-sm text-gray-500 mt-1">
                  Last Activity: {formatDate(member.lastActivity)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-blue-900">Groups</h3>
                    <p className="text-3xl font-bold text-blue-600">{member.groups.length}</p>
                    <p className="text-sm text-blue-700">Active memberships</p>
                  </div>
                  
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-green-900">Serving Teams</h3>
                    <p className="text-3xl font-bold text-green-600">{member.serving.length}</p>
                    <p className="text-sm text-green-700">Current teams</p>
                  </div>
                  
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-yellow-900">Giving Years</h3>
                    <p className="text-3xl font-bold text-yellow-600">{member.giving.length}</p>
                    <p className="text-sm text-yellow-700">Years with gifts</p>
                  </div>
                  
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-purple-900">Discover Grace</h3>
                    <p className="text-3xl font-bold text-purple-600">
                      {member.discoverGrace.filter(dg => dg.attended).length}/{member.discoverGrace.length}
                    </p>
                    <p className="text-sm text-purple-700">Sessions attended</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                    <div className="space-y-3">
                      {member.attendance.slice(0, 5).map((event, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{event.eventName}</p>
                            <p className="text-sm text-gray-500">{formatDate(event.eventDate)}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            event.attended ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {event.attended ? 'Attended' : 'Missed'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Giving Summary</h3>
                    <div className="space-y-3">
                      {member.giving.slice(0, 3).map((year, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{year.year}</p>
                            <p className="text-sm text-gray-500">{year.frequency} gifts</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900">{formatCurrency(year.totalAmount)}</p>
                            <p className="text-sm text-gray-500">Avg: {formatCurrency(year.averageGift)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'groups' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Group Participation</h3>
                {member.groups.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No group memberships found.</p>
                ) : (
                  <div className="space-y-4">
                    {member.groups.map((group, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-semibold text-gray-900">{group.groupName}</h4>
                            <p className="text-sm text-gray-500">Role: {group.role}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            group.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {group.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Joined</p>
                            <p className="font-medium">{formatDate(group.joinedDate)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Meetings Attended</p>
                            <p className="font-medium">{group.meetingsAttended}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Total Meetings</p>
                            <p className="font-medium">{group.totalMeetings}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Attendance Rate</p>
                            <p className="font-medium">{group.attendanceRate}%</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'serving' && (
              <ServiceHistory 
                serviceHistory={member.serving} 
                memberName={`${member.firstName} ${member.lastName}`}
              />
            )}

            {activeTab === 'giving' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Giving History</h3>
                {member.giving.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No giving history found.</p>
                ) : (
                  <div className="space-y-4">
                    {member.giving.map((year, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-gray-900">{year.year}</h4>
                          <p className="text-lg font-bold text-green-600">{formatCurrency(year.totalAmount)}</p>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Gifts</p>
                            <p className="font-medium">{year.frequency}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Average Gift</p>
                            <p className="font-medium">{formatCurrency(year.averageGift)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Last Gift</p>
                            <p className="font-medium">{formatDate(year.lastGift)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'attendance' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Recent Attendance</h3>
                {member.attendance.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No attendance records found.</p>
                ) : (
                  <div className="space-y-3">
                    {member.attendance.map((event, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{event.eventName}</p>
                          <p className="text-sm text-gray-500">{formatDate(event.eventDate)} • {event.eventType}</p>
                        </div>
                        <span className={`px-3 py-1 text-sm rounded-full ${
                          event.attended ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {event.attended ? 'Attended' : 'Missed'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'discoverGrace' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Discover Grace Attendance</h3>
                {member.discoverGrace.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No Discover Grace sessions found.</p>
                ) : (
                  <div className="space-y-3">
                    {member.discoverGrace.map((session, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">Session {session.sessionNumber}</p>
                          <p className="text-sm text-gray-500">{formatDate(session.sessionDate)}</p>
                        </div>
                        <div className="flex space-x-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            session.registered ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {session.registered ? 'Registered' : 'Not Registered'}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            session.attended ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {session.attended ? 'Attended' : 'Missed'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberDetail;
